import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { MessageCircle, X, Send, Bot, User as UserIcon, Loader2, Trash2, ArrowRight, Phone, Receipt, CreditCard, Banknote, Calculator, ShieldCheck, TrendingUp, FileText, Gift, ChevronLeft } from 'lucide-react';
import { API } from '../App';

const STORAGE_KEY_SID = 'arthik_chat_sid';
const STORAGE_KEY_OPEN = 'arthik_chat_open';

// All the things a customer can enquire about — grouped by area.
// Clicking a chip pre-fills the input so the LLM gets a clean prompt.
const TOPIC_CATALOGUE = [
  {
    key: 'loans', label: 'Loans', icon: Banknote, color: '#FF6B35',
    blurb: 'Home · Personal · Car · Education · Business',
    prompts: [
      'How do I apply for a Home Loan?',
      'What is the eligibility for a ₹10 lakh Personal Loan?',
      'Which bank offers the lowest Car Loan rate?',
      'What documents do I need for an Education Loan?',
      'Compare HDFC, ICICI and SBI home loan rates',
      'Calculate my EMI for ₹25L at 8.5% for 20 years',
    ],
  },
  {
    key: 'credit-cards', label: 'Credit Cards', icon: CreditCard, color: '#3B82F6',
    blurb: 'Compare · Rewards · Lounge · Fuel',
    prompts: [
      'Which credit card is best for travel rewards?',
      'Show me lifetime-free credit cards',
      'How does the joining bonus work?',
      'Best card for fuel cashback under ₹2500 annual fee',
    ],
  },
  {
    key: 'tax', label: 'Tax Services', icon: Receipt, color: '#10B981',
    blurb: 'ITR · GST · TDS · Tax planning · Biz registration',
    prompts: [
      'How do I file my ITR for FY 24-25?',
      'What is the difference between old and new tax regime?',
      'GST registration cost & process',
      'I want to register a Private Limited company',
      'How much will I save under section 80C?',
      'Connect me with a Chartered Accountant',
    ],
  },
  {
    key: 'credit-score', label: 'Credit Score', icon: TrendingUp, color: '#8B5CF6',
    blurb: 'Free CIBIL · Improve score · Loan impact',
    prompts: [
      'Check my credit score (PAN-based)',
      'How can I improve my CIBIL score?',
      'Why was my loan rejected?',
      'What credit score do I need for a home loan?',
    ],
  },
  {
    key: 'calculators', label: 'Calculators', icon: Calculator, color: '#F59E0B',
    blurb: 'EMI · SIP · FD · RD · Tax · Eligibility',
    prompts: [
      'Calculate SIP returns for ₹5000/month for 15 years',
      'FD calculator for ₹2 lakh at 7% for 3 years',
      'Tax saved if I invest ₹1.5L in 80C',
      'Loan eligibility on ₹70,000 monthly income',
    ],
  },
  {
    key: 'insurance', label: 'Insurance', icon: ShieldCheck, color: '#EF4444',
    blurb: 'Health · Life · Term · Vehicle · Travel',
    prompts: [
      'Best term insurance for a 30-year old non-smoker',
      'How much health cover do I need for my family?',
      'Compare car insurance quotes',
    ],
  },
  {
    key: 'investments', label: 'Investments', icon: TrendingUp, color: '#0EA5E9',
    blurb: 'FD · RD · SIP · Mutual Funds',
    prompts: [
      'Which is better — SIP or lump sum?',
      'Where should I park ₹5 lakh for 2 years?',
      'Best mutual funds for long-term wealth',
    ],
  },
  {
    key: 'application', label: 'My Application', icon: FileText, color: '#64748B',
    blurb: 'Status · Documents · Next steps',
    prompts: [
      'How do I check my loan application status?',
      'Which documents are still pending on my case?',
      'How long does loan approval take?',
    ],
  },
  {
    key: 'referrals', label: 'Refer & Earn', icon: Gift, color: '#EC4899',
    blurb: 'Refer friends · Earn cashback',
    prompts: [
      'How does the referral program work?',
      'How much can I earn per referral?',
      'Where is my referral code?',
    ],
  },
];

function newSessionId() {
  return 'web_' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [activeTopic, setActiveTopic] = useState(null); // null => show category grid
  const scrollRef = useRef(null);

  // Initialize session + history
  useEffect(() => {
    let sid = localStorage.getItem(STORAGE_KEY_SID);
    if (!sid) {
      sid = newSessionId();
      localStorage.setItem(STORAGE_KEY_SID, sid);
    }
    setSessionId(sid);
    (async () => {
      try {
        const res = await axios.get(`${API}/chat/history/${sid}`);
        setMessages(res.data || []);
      } catch (_e) { /* ignore */ }
    })();
    if (localStorage.getItem(STORAGE_KEY_OPEN) === '1') setOpen(true);
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_OPEN, open ? '1' : '0');
  }, [open]);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const send = async (e, overrideText) => {
    e?.preventDefault?.();
    const text = (overrideText ?? input).trim();
    if (!text || sending || !sessionId) return;
    setInput('');
    setError(null);
    const now = new Date().toISOString();
    setMessages((m) => [...m, { role: 'user', text, created_at: now }]);
    setSending(true);
    try {
      const res = await axios.post(`${API}/chat`, { session_id: sessionId, message: text });
      setMessages((m) => [...m, { role: 'assistant', text: res.data.reply, created_at: new Date().toISOString() }]);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Chat failed';
      setError(detail);
    } finally {
      setSending(false);
    }
  };

  const clearChat = async () => {
    if (!sessionId) return;
    try { await axios.delete(`${API}/chat/history/${sessionId}`); } catch (_e) { /* ignore */ }
    const sid = newSessionId();
    localStorage.setItem(STORAGE_KEY_SID, sid);
    setSessionId(sid);
    setMessages([]);
    setError(null);
    setActiveTopic(null);
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="chat-fab"
          aria-label="Open chat assistant"
          style={{
            position: 'fixed',
            bottom: 'calc(env(safe-area-inset-bottom, 0) + 84px)',
            right: 20,
            width: 60,
            height: 60,
            borderRadius: 999,
            border: 'none',
            background: 'linear-gradient(135deg,#FF6B35 0%,#E55100 100%)',
            color: '#fff',
            cursor: 'pointer',
            boxShadow: '0 10px 24px rgba(255,107,53,0.45)',
            zIndex: 95,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <MessageCircle size={26} />
        </button>
      )}
      {open && (
        <div
          data-testid="chat-widget"
          style={{
            position: 'fixed',
            bottom: 'calc(env(safe-area-inset-bottom, 0) + 84px)',
            right: 16,
            width: 'min(380px, calc(100vw - 32px))',
            height: 'min(560px, calc(100vh - 120px))',
            background: '#fff',
            borderRadius: 18,
            boxShadow: '0 18px 40px rgba(15,23,42,0.25)',
            zIndex: 96,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            border: '1px solid #E2E8F0',
          }}
        >
          {/* Header */}
          <div style={{ background: 'linear-gradient(135deg,#FF6B35 0%,#E55100 100%)', color: '#fff', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 999, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Bot size={20} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 800, fontSize: 15 }}>Arthik Assistant</div>
              <div style={{ fontSize: 11, opacity: 0.9 }}>Online · AI-powered</div>
            </div>
            <button onClick={clearChat} aria-label="Clear chat" title="Clear chat" data-testid="chat-clear" style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', opacity: 0.85 }}>
              <Trash2 size={16} />
            </button>
            <button onClick={() => setOpen(false)} aria-label="Close chat" data-testid="chat-close" style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer' }}>
              <X size={20} />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} style={{ flex: 1, padding: '14px 14px 8px', overflowY: 'auto', background: '#F8FAFC' }}>
            {messages.length === 0 && (
              <div style={{ color: '#475569' }} data-testid="chat-topic-browser">
                <div style={{ fontSize: 14, lineHeight: 1.5, marginBottom: 12 }}>
                  Hi 👋 I&apos;m <strong>Arthik Assistant</strong>. What can I help you with?
                </div>

                {!activeTopic ? (
                  <>
                    <div style={{ fontSize: 11, fontWeight: 800, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Browse by topic</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 }}>
                      {TOPIC_CATALOGUE.map((t) => {
                        const Icon = t.icon;
                        return (
                          <button
                            key={t.key}
                            onClick={() => setActiveTopic(t)}
                            data-testid={`chat-topic-${t.key}`}
                            style={{
                              background: 'white', border: '1px solid #E2E8F0', borderRadius: 12,
                              padding: '10px 11px', textAlign: 'left', cursor: 'pointer',
                              display: 'flex', flexDirection: 'column', gap: 4,
                              transition: 'all 0.15s ease',
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.borderColor = t.color; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E2E8F0'; e.currentTarget.style.transform = 'translateY(0)'; }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ width: 22, height: 22, borderRadius: 6, background: `${t.color}1a`, color: t.color, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Icon size={13} />
                              </span>
                              <span style={{ fontWeight: 800, fontSize: 12.5, color: '#0F172A' }}>{t.label}</span>
                            </div>
                            <div style={{ fontSize: 10.5, color: '#64748B', lineHeight: 1.35 }}>{t.blurb}</div>
                          </button>
                        );
                      })}
                    </div>

                    <div style={{ fontSize: 11, fontWeight: 800, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Talk to a human</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <a href="/enquiry" data-testid="chat-cta-enquiry" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 11px', background: '#FFF7ED', color: '#9A3412', border: '1px solid #FED7AA', borderRadius: 10, fontSize: 12.5, fontWeight: 700, textDecoration: 'none' }}>
                        <FileText size={14} /> Submit a Quick Enquiry — advisor calls within 24 h
                        <ArrowRight size={14} style={{ marginLeft: 'auto' }} />
                      </a>
                      <a href="/tax-services" data-testid="chat-cta-ca" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 11px', background: '#ECFDF5', color: '#065F46', border: '1px solid #A7F3D0', borderRadius: 10, fontSize: 12.5, fontWeight: 700, textDecoration: 'none' }}>
                        <Receipt size={14} /> File ITR / GST with a verified CA
                        <ArrowRight size={14} style={{ marginLeft: 'auto' }} />
                      </a>
                      <a href="tel:+917054986771" data-testid="chat-cta-call" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 11px', background: '#EFF6FF', color: '#1E40AF', border: '1px solid #BFDBFE', borderRadius: 10, fontSize: 12.5, fontWeight: 700, textDecoration: 'none' }}>
                        <Phone size={14} /> Call us · +91 70549 86771
                      </a>
                    </div>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => setActiveTopic(null)}
                      data-testid="chat-topic-back"
                      style={{ background: 'transparent', border: 'none', color: '#64748B', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, padding: 0, marginBottom: 8 }}
                    >
                      <ChevronLeft size={14} /> All topics
                    </button>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', background: `${activeTopic.color}1a`, color: activeTopic.color, borderRadius: 999, fontWeight: 800, fontSize: 12, marginBottom: 10 }}>
                      <activeTopic.icon size={13} /> {activeTopic.label}
                    </div>
                    <div style={{ fontSize: 11.5, color: '#64748B', marginBottom: 8 }}>Pick a question or type your own at the bottom:</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {activeTopic.prompts.map((p) => (
                        <button
                          key={p}
                          onClick={() => send(null, p)}
                          data-testid={`chat-prompt-${activeTopic.key}-${p.slice(0, 12)}`}
                          style={{
                            background: 'white', border: '1px solid #E2E8F0', borderRadius: 10,
                            padding: '8px 11px', textAlign: 'left', cursor: 'pointer', fontSize: 12.5, color: '#0F172A',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6,
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#FFF7ED'; e.currentTarget.style.borderColor = '#FED7AA'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; e.currentTarget.style.borderColor = '#E2E8F0'; }}
                        >
                          <span>{p}</span>
                          <ArrowRight size={12} style={{ color: '#94A3B8', flexShrink: 0 }} />
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
            {messages.map((m, i) => (
              <Message key={i} role={m.role} text={m.text} />
            ))}
            {sending && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', color: '#FF6B35', fontSize: 13, padding: '6px 0' }}>
                <Loader2 size={16} className="spin" /> Thinking…
              </div>
            )}
            {error && (
              <div data-testid="chat-error" style={{ background: '#FEE2E2', color: '#991B1B', padding: '8px 10px', borderRadius: 8, fontSize: 12, marginTop: 6 }}>
                {error.includes('Budget') ? 'AI quota reached. Please ask the site owner to top up the Emergent LLM key (Profile → Universal Key → Add Balance).' : error}
              </div>
            )}
          </div>

          {/* Input */}
          <form onSubmit={send} style={{ borderTop: '1px solid #E2E8F0', padding: 10, background: '#fff', display: 'flex', gap: 8 }}>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask anything…"
              data-testid="chat-input"
              disabled={sending}
              style={{ flex: 1, border: '1px solid #E2E8F0', borderRadius: 999, padding: '10px 14px', outline: 'none', fontSize: 14 }}
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              data-testid="chat-send"
              style={{ background: 'linear-gradient(135deg,#FF6B35 0%,#E55100 100%)', color: '#fff', border: 'none', borderRadius: 999, width: 44, height: 44, cursor: sending || !input.trim() ? 'not-allowed' : 'pointer', opacity: sending || !input.trim() ? 0.5 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <Send size={16} />
            </button>
          </form>
        </div>
      )}
      <style>{`
        @keyframes arthik-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .spin { animation: arthik-spin 1s linear infinite; }
      `}</style>
    </>
  );
};

const Message = ({ role, text }) => {
  const isUser = role === 'user';
  return (
    <div style={{ display: 'flex', gap: 8, margin: '8px 0', flexDirection: isUser ? 'row-reverse' : 'row' }}>
      <div style={{ width: 28, height: 28, borderRadius: 999, background: isUser ? '#3B82F6' : '#FF6B35', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {isUser ? <UserIcon size={14} /> : <Bot size={14} />}
      </div>
      <div
        data-testid={isUser ? 'chat-msg-user' : 'chat-msg-bot'}
        style={{
          background: isUser ? '#3B82F6' : '#fff',
          color: isUser ? '#fff' : '#0F172A',
          border: isUser ? 'none' : '1px solid #E2E8F0',
          padding: '10px 12px',
          borderRadius: isUser ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
          fontSize: 13.5,
          lineHeight: 1.55,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          maxWidth: '78%',
        }}
      >
        {text}
      </div>
    </div>
  );
};

export default ChatWidget;
